(() => {
  const normalize = (value) => String(value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

  class OllaProductPage {
    constructor(root) {
      if (root.dataset.ollaReady === "true") return;
      root.dataset.ollaReady = "true";

      this.root = root;
      this.milestoneMax = 4;
      this.bundleMode = root.dataset.bundleMode || "bundle_variants";
      this.showSubscription = root.dataset.showSubscription === "true";
      this.initialMode = root.dataset.initialPurchaseMode || "subscription";
      this.addLabel = root.dataset.addLabel || "Add bundle to cart";
      this.oneTimePrices = this.parsePrices(root.dataset.oneTimePrices);
      this.subscriptionPrices = this.parsePrices(root.dataset.subscriptionPrices);
      this.product = this.readProductData();
      this.flavors = [...root.querySelectorAll("[data-olla-flavor]")].map((element) => ({
        element,
        id: element.dataset.flavorId,
        name: element.dataset.flavorName,
        shortName: element.dataset.flavorShortName || element.dataset.flavorName,
        variantTitle: element.dataset.variantTitle || element.dataset.flavorName,
        quantity: Math.max(0, Number(element.dataset.defaultQuantity || 0)),
      }));

      this.mode = this.initialMode;
      this.loading = false;
      this.bindEvents();
      this.normalizeInitialSelection();
      this.update();
    }

    parsePrices(value) {
      const entries = String(value || "").split(",");
      return Object.fromEntries(entries.map((entry, index) => [index + 1, Math.round(Number(entry || 0) * 100)]));
    }

    readProductData() {
      const node = this.root.querySelector("[data-olla-product-json]");
      if (!node) return { cartUrl: "/cart", variants: [] };
      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error("Olla product data could not be read.", error);
        return { cartUrl: "/cart", variants: [] };
      }
    }

    bindEvents() {
      this.flavors.forEach((flavor) => {
        flavor.element.querySelector("[data-olla-add-flavor]")?.addEventListener("click", () => this.changeFlavor(flavor.id, 1));
        flavor.element.querySelector("[data-olla-minus]")?.addEventListener("click", () => this.changeFlavor(flavor.id, -1));
        flavor.element.querySelector("[data-olla-plus]")?.addEventListener("click", () => this.changeFlavor(flavor.id, 1));
      });

      this.root.querySelectorAll('input[name^="purchase-mode-"]').forEach((input) => {
        input.addEventListener("change", () => {
          if (input.checked && !input.disabled) {
            this.mode = input.value;
            this.update();
          }
        });
      });

      this.root.querySelector("[data-olla-add]")?.addEventListener("click", (event) => this.addToCart(event.currentTarget));
      this.root.querySelectorAll("[data-olla-scroll]").forEach((button) => button.addEventListener("click", () => this.scrollToBuilder()));
      this.root.querySelectorAll("[data-olla-choose-flavor]").forEach((button) => {
        button.addEventListener("click", () => {
          const target = button.dataset.ollaChooseFlavor;
          this.flavors.forEach((flavor) => { flavor.quantity = flavor.id === target ? 1 : 0; });
          this.update();
          this.scrollToBuilder();
        });
      });

      this.root.querySelector("[data-olla-details-toggle]")?.addEventListener("click", (event) => {
        const panel = event.currentTarget.closest(".productDetails");
        const open = panel.classList.toggle("open");
        event.currentTarget.setAttribute("aria-expanded", String(open));
      });

      this.root.querySelectorAll("[data-olla-faq-toggle]").forEach((button) => {
        button.addEventListener("click", () => {
          const item = button.closest("article");
          const open = item.classList.toggle("open");
          button.setAttribute("aria-expanded", String(open));
        });
      });
    }

    normalizeInitialSelection() {
      this.flavors.forEach((flavor) => {
        flavor.quantity = Math.max(0, flavor.quantity);
      });
    }

    get total() {
      return this.flavors.reduce((sum, flavor) => sum + flavor.quantity, 0);
    }

    changeFlavor(id, delta) {
      const flavor = this.flavors.find((item) => item.id === id);
      if (!flavor) return;
      flavor.quantity = Math.max(0, flavor.quantity + delta);
      this.update();
    }

    findBundleVariant(size) {
      const variants = this.product.variants || [];
      const candidates = [
        `${size} bottle`,
        `${size} bottles`,
        `${size} bottle bundle`,
        `${size} pack`,
        `${size} pack bundle`,
      ];
      const matched = variants.find((variant) => {
        const title = normalize([variant.title, ...(variant.options || [])].join(" "));
        return candidates.some((candidate) => title === normalize(candidate) || title.includes(normalize(candidate)));
      });
      return matched || null;
    }

    findFlavorVariant(flavor) {
      const target = normalize(flavor.variantTitle || flavor.name);
      const matches = (this.product.variants || []).filter((variant) => {
        const optionMatch = (variant.options || []).some((option) => normalize(option) === target);
        const title = normalize(variant.title);
        return optionMatch || title === target || title.includes(target);
      });
      const singleBottleMatches = matches.filter((variant) => {
        const values = [variant.title, ...(variant.options || [])].map(normalize);
        return values.some((value) => value === "1 bottle" || value === "single bottle" || value.includes("1 bottle"));
      });
      return singleBottleMatches.find((variant) => variant.available !== false)
        || matches.find((variant) => variant.available !== false)
        || singleBottleMatches[0]
        || matches[0]
        || null;
    }

    getSellingPlan(variant) {
      return variant?.sellingPlans?.[0] || this.product.fallbackSellingPlan || null;
    }

    getConfiguredPrice(table, size) {
      return Number(table[size] || 0);
    }

    getBundleSizes(total = this.total) {
      const sizes = [];
      let remaining = Math.max(0, Number(total || 0));
      while (remaining > 0) {
        const size = Math.min(this.milestoneMax, remaining);
        sizes.push(size);
        remaining -= size;
      }
      return sizes;
    }

    getConfiguredTotal(table, total = this.total) {
      return this.getBundleSizes(total).reduce((sum, size) => sum + this.getConfiguredPrice(table, size), 0);
    }

    getTierTotal(size, mode = this.mode) {
      if (this.bundleMode === "bundle_variants") {
        return mode === "subscription"
          ? this.getConfiguredPrice(this.subscriptionPrices, size)
          : this.getConfiguredPrice(this.oneTimePrices, size);
      }
      return mode === "subscription"
        ? this.getConfiguredPrice(this.subscriptionPrices, size)
        : this.getConfiguredPrice(this.oneTimePrices, size);
    }

    getOneTimeTotal() {
      if (this.total === 0) return 0;
      if (this.bundleMode === "bundle_variants") {
        return this.getConfiguredTotal(this.oneTimePrices);
      }
      return this.getConfiguredTotal(this.oneTimePrices) || this.flavors.reduce((sum, flavor) => {
        const variant = this.findFlavorVariant(flavor);
        return sum + ((variant?.price || 0) * flavor.quantity);
      }, 0);
    }

    getSubscriptionTotal() {
      if (this.total === 0) return 0;
      if (this.bundleMode === "bundle_variants") {
        return this.getConfiguredTotal(this.subscriptionPrices);
      }

      const allocationTotal = this.flavors.reduce((sum, flavor) => {
        const variant = this.findFlavorVariant(flavor);
        return sum + ((this.getSellingPlan(variant)?.price || 0) * flavor.quantity);
      }, 0);
      return this.getConfiguredTotal(this.subscriptionPrices) || allocationTotal;
    }

    selectionSupportsSubscription() {
      if (!this.showSubscription) return false;
      if (this.total === 0) return true;
      if (this.bundleMode === "bundle_variants") {
        return this.getBundleSizes().every((size) => {
          const variant = this.findBundleVariant(size);
          return Boolean(variant && this.getSellingPlan(variant));
        });
      }
      return this.flavors
        .filter((flavor) => flavor.quantity > 0)
        .every((flavor) => {
          const variant = this.findFlavorVariant(flavor);
          return Boolean(variant && this.getSellingPlan(variant));
        });
    }

    selectionIsAvailable() {
      if (this.total === 0) return false;
      if (this.bundleMode === "bundle_variants") {
        return this.getBundleSizes().every((size) => Boolean(this.findBundleVariant(size)?.available));
      }
      return this.flavors
        .filter((flavor) => flavor.quantity > 0)
        .every((flavor) => Boolean(this.findFlavorVariant(flavor)));
    }

    money(cents) {
      const currency = window.Shopify?.currency?.active || "USD";
      try {
        return new Intl.NumberFormat(document.documentElement.lang || "en-US", {
          style: "currency",
          currency,
          minimumFractionDigits: 2,
        }).format(Number(cents || 0) / 100);
      } catch (_) {
        return `$${(Number(cents || 0) / 100).toFixed(2)}`;
      }
    }

    percentOff(price, quantity) {
      const single = this.getConfiguredPrice(this.oneTimePrices, 1);
      if (!single || !quantity) return 0;
      return Math.max(0, Math.round(((single * quantity - price) / (single * quantity)) * 100));
    }

    summary() {
      return this.flavors
        .filter((flavor) => flavor.quantity > 0)
        .map((flavor) => `${flavor.shortName}${flavor.quantity > 1 ? ` ×${flavor.quantity}` : ""}`)
        .join(", ");
    }

    update() {
      const total = this.total;
      const oneTime = this.getOneTimeTotal();
      const subscription = this.getSubscriptionTotal();
      const subscriptionSupported = this.selectionSupportsSubscription();
      const selectionAvailable = this.selectionIsAvailable();
      const subscriptionOption = this.root.querySelector('[data-olla-purchase-option="subscription"]');
      const subscriptionInput = subscriptionOption?.querySelector("input");
      const oneTimeOption = this.root.querySelector('[data-olla-purchase-option="one-time"]');
      const oneTimeInput = oneTimeOption?.querySelector("input");
      if (!subscriptionSupported && this.mode === "subscription") this.mode = "one-time";
      if (subscriptionOption) {
        subscriptionOption.hidden = !this.showSubscription;
        subscriptionOption.classList.toggle("unavailable", !subscriptionSupported);
        subscriptionOption.setAttribute("aria-disabled", String(!subscriptionSupported));
      }
      if (subscriptionInput) {
        subscriptionInput.disabled = !subscriptionSupported;
        subscriptionInput.checked = this.mode === "subscription";
      }
      if (oneTimeInput) oneTimeInput.checked = this.mode === "one-time";
      subscriptionOption?.classList.toggle("active", this.mode === "subscription");
      oneTimeOption?.classList.toggle("active", this.mode === "one-time");

      this.flavors.forEach((flavor) => {
        flavor.element.classList.toggle("selected", flavor.quantity > 0);
        const addFlavor = flavor.element.querySelector("[data-olla-add-flavor]");
        const quantityControl = flavor.element.querySelector(".quantityControl");
        const quantity = flavor.element.querySelector("[data-olla-quantity]");
        const minus = flavor.element.querySelector("[data-olla-minus]");
        const plus = flavor.element.querySelector("[data-olla-plus]");
        if (addFlavor) {
          addFlavor.hidden = flavor.quantity > 0;
          addFlavor.disabled = false;
        }
        if (quantityControl) quantityControl.hidden = flavor.quantity === 0;
        if (quantity) quantity.textContent = flavor.quantity;
        if (minus) minus.disabled = flavor.quantity === 0;
        if (plus) plus.disabled = false;
      });

      const spots = this.root.querySelector("[data-olla-spots-left]");
      if (spots) {
        spots.textContent = total > 0
          ? `${total} bottle${total === 1 ? "" : "s"} selected`
          : "Add as many as you like";
      }

      const activePrice = this.mode === "subscription" ? subscription : oneTime;
      const displayQuantity = total || 1;
      const displayOneTimeTotal = total ? oneTime : this.getConfiguredPrice(this.oneTimePrices, 1);
      const displaySubscriptionTotal = total ? subscription : this.getConfiguredPrice(this.subscriptionPrices, 1);
      const oneTimeUnitPrice = displayOneTimeTotal / displayQuantity;
      const subscriptionUnitPrice = displaySubscriptionTotal / displayQuantity;
      const firstOrderSavingsPercent = oneTimeUnitPrice > 0 && subscriptionUnitPrice < oneTimeUnitPrice
        ? Math.round(((oneTimeUnitPrice - subscriptionUnitPrice) / oneTimeUnitPrice) * 100)
        : 0;
      const subscriptionBenefits = this.root.querySelector(".subscriptionBenefits");
      if (subscriptionBenefits) subscriptionBenefits.hidden = !this.showSubscription;

      const track = this.root.querySelector(".bundleTrack");
      if (track) {
        const progressTotal = Math.min(total, this.milestoneMax);
        track.style.setProperty("--bundle-progress", `${progressTotal <= 1 ? 0 : ((progressTotal - 1) / (this.milestoneMax - 1)) * 100}%`);
        track.setAttribute("aria-valuenow", String(total));
        track.setAttribute("aria-valuetext", `${total} bottle${total === 1 ? "" : "s"} selected`);
      }
      this.root.querySelectorAll("[data-olla-milestone]").forEach((milestone) => {
        const size = Number(milestone.dataset.ollaMilestone);
        milestone.classList.toggle("reached", total >= size);
        const isActive = size === this.milestoneMax ? total >= size : total === size;
        milestone.classList.toggle("active", isActive);
        const tierTotal = this.getTierTotal(size);
        const tierPrice = milestone.querySelector("[data-olla-tier-price]");
        if (tierPrice) tierPrice.textContent = tierTotal ? `${this.money(tierTotal / size)}/ea` : "—";
        if (isActive) milestone.setAttribute("aria-current", "step");
        else milestone.removeAttribute("aria-current");
      });

      this.setUnitPrice("[data-olla-one-time-unit-price]", oneTimeUnitPrice);
      this.setUnitPrice("[data-olla-subscription-unit-price]", subscriptionUnitPrice);
      this.setText("[data-olla-subscription-compare-price]", this.money(oneTimeUnitPrice));
      this.setText(
        "[data-olla-subscription-badge]",
        firstOrderSavingsPercent > 0 ? `SAVE ${firstOrderSavingsPercent}% ON FIRST ORDER` : "SUBSCRIBE & SAVE"
      );
      this.setText("[data-olla-summary]", this.summary() || "Choose your first flavor");
      this.setText("[data-olla-mobile-total]", total ? this.money(activePrice) : "Start");

      const addButton = this.root.querySelector("[data-olla-add]");
      if (addButton) {
        addButton.disabled = total === 0 || !selectionAvailable || this.loading;
        addButton.textContent = this.loading
          ? "Adding…"
          : total === 0
            ? "Add at least one bottle"
            : !selectionAvailable
              ? "Selected bundle unavailable"
              : `${this.addLabel} — ${this.money(activePrice)}`;
      }
    }

    setText(selector, value) {
      const element = this.root.querySelector(selector);
      if (element) element.textContent = value;
    }

    setUnitPrice(selector, cents) {
      const element = this.root.querySelector(selector);
      if (!element) return;
      element.textContent = this.money(cents);
      const suffix = document.createElement("small");
      suffix.textContent = "/ea";
      element.appendChild(suffix);
    }

    scrollToBuilder() {
      const mobile = window.matchMedia?.("(max-width: 840px)")?.matches;
      const target = mobile
        ? (this.root.querySelector(".productInfo") || this.root.querySelector("[data-olla-builder]"))
        : this.root.querySelector("[data-olla-builder]");
      target?.scrollIntoView({ behavior: "smooth", block: "start" });
    }

    buildCartItems() {
      if (this.bundleMode === "bundle_variants") {
        const units = this.flavors.flatMap((flavor) => Array.from({ length: flavor.quantity }, () => flavor.shortName));
        const chunks = [];
        for (let index = 0; index < units.length; index += this.milestoneMax) {
          chunks.push(units.slice(index, index + this.milestoneMax));
        }

        return chunks.map((flavorNames) => {
          const size = flavorNames.length;
          const variant = this.findBundleVariant(size);
          if (!variant) throw new Error(`Create a ${size}-bottle Shopify variant before adding this bundle.`);

          const counts = flavorNames.reduce((result, name) => {
            result[name] = (result[name] || 0) + 1;
            return result;
          }, {});
          const flavorSummary = Object.entries(counts)
            .map(([name, quantity]) => `${name}${quantity > 1 ? ` ×${quantity}` : ""}`)
            .join(", ");
          const expectedPrice = this.getConfiguredPrice(
            this.mode === "subscription" ? this.subscriptionPrices : this.oneTimePrices,
            size
          );
          const plan = this.getSellingPlan(variant);
          const actualPrice = this.mode === "subscription" ? plan?.price : variant.price;

          if (this.mode === "subscription" && !plan) {
            throw new Error(`The ${size}-bottle bundle does not have an active subscription plan yet.`);
          }
          if (expectedPrice && actualPrice != null && Number(actualPrice) !== expectedPrice) {
            throw new Error(
              `Shopify is charging ${this.money(actualPrice)} for the ${size}-bottle ${this.mode === "subscription" ? "subscription" : "one-time"} bundle. Update that Shopify variant to ${this.money(expectedPrice)} so checkout matches this page.`
            );
          }

          const item = {
            id: variant.id,
            quantity: 1,
            properties: { "Selected flavors": flavorSummary },
          };
          if (this.mode === "subscription") item.selling_plan = plan.id;
          return item;
        });
      }

      return this.flavors
        .filter((flavor) => flavor.quantity > 0)
        .map((flavor) => {
          const variant = this.findFlavorVariant(flavor);
          if (!variant) throw new Error(`No Shopify variant matches ${flavor.name}.`);
          const item = { id: variant.id, quantity: flavor.quantity };
          if (this.mode === "subscription") {
            const plan = this.getSellingPlan(variant);
            if (!plan) throw new Error(`${flavor.name} does not have an active subscription plan yet.`);
            item.selling_plan = plan.id;
          }
          return item;
        });
    }

    async addToCart(button) {
      if (this.loading || this.total === 0) return;
      this.showMessage("");
      this.loading = true;
      this.update();

      try {
        const items = this.buildCartItems();
        const cart = document.querySelector("cart-drawer") || document.querySelector("cart-notification");
        const sections = cart?.getSectionsToRender?.().map((section) => section.id) || [];
        cart?.setActiveElement?.(button);

        const response = await fetch(`${window.Shopify?.routes?.root || "/"}cart/add.js`, {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-Requested-With": "XMLHttpRequest" },
          body: JSON.stringify({ items, sections, sections_url: window.location.pathname }),
        });
        const state = await response.json();
        if (!response.ok || state.status) throw new Error(state.description || state.message || "This bundle could not be added.");

        if (cart?.open && sections.includes("cart-drawer")) {
          await this.refreshCartDrawer(cart, state, sections, button);
        } else {
          window.location.assign(this.product.cartUrl || `${window.Shopify?.routes?.root || "/"}cart`);
        }
      } catch (error) {
        console.error(error);
        this.showMessage(error.message || "This bundle could not be added. Please try again.", true);
      } finally {
        this.loading = false;
        this.update();
      }
    }

    async refreshCartDrawer(cart, addState, sectionIds, button) {
      const root = window.Shopify?.routes?.root || "/";
      const sectionUrl = new URL(window.location.pathname, window.location.origin);
      sectionUrl.searchParams.set("sections", sectionIds.join(","));
      sectionUrl.searchParams.set("olla_cart_refresh", String(Date.now()));

      let renderedSections = addState.sections || {};

      // A second section request is intentional. Shopify's multi-item Cart API
      // can return drawer HTML from the pre-add cart on some theme/app stacks.
      // Reloading after the add has completed guarantees that the response is
      // built from the current cart and contains every selected flavor.
      try {
        const sectionResponse = await fetch(sectionUrl.toString(), {
          method: "GET",
          cache: "no-store",
          headers: { "X-Requested-With": "XMLHttpRequest" },
        });
        if (sectionResponse.ok) {
          const freshSections = await sectionResponse.json();
          if (freshSections?.["cart-drawer"]) renderedSections = freshSections;
        }
      } catch (error) {
        console.warn("Fresh cart drawer sections could not be loaded; using the add response.", error);
      }

      const drawerHtml = renderedSections["cart-drawer"];
      const currentDrawer = document.getElementById("CartDrawer");
      const nextDrawer = drawerHtml
        ? new DOMParser().parseFromString(drawerHtml, "text/html").querySelector("#CartDrawer")
        : null;

      if (!currentDrawer || !nextDrawer) {
        window.location.assign(this.product.cartUrl || `${root}cart`);
        return;
      }

      // Replace the drawer directly instead of relying on Shrine's generic
      // renderer. This avoids preserving empty-cart markup/classes around the
      // newly added products, which is what produced the blank white panel.
      currentDrawer.innerHTML = nextDrawer.innerHTML;

      const iconHtml = renderedSections["cart-icon-bubble"];
      const currentIcon = document.getElementById("cart-icon-bubble");
      const nextIcon = iconHtml
        ? new DOMParser().parseFromString(iconHtml, "text/html").querySelector("#cart-icon-bubble")
        : null;
      if (currentIcon && nextIcon) currentIcon.innerHTML = nextIcon.innerHTML;

      cart.classList.remove("is-empty");
      cart.querySelector(".drawer__inner")?.classList.remove("is-empty");
      cart.querySelector("cart-drawer-items")?.classList.remove("is-empty");

      const overlay = cart.querySelector("#CartDrawer-Overlay");
      overlay?.addEventListener("click", () => cart.close());

      // Never leave customers inside an empty panel. If a third-party app has
      // prevented Shopify from returning line-item markup, the full cart page is
      // the safe checkout-ready fallback.
      if (!cart.querySelector(".cart-item")) {
        window.location.assign(this.product.cartUrl || `${root}cart`);
        return;
      }

      cart.setActiveElement?.(button);
      cart.open(button);
    }

    showMessage(message, isError = false) {
      const element = this.root.querySelector("[data-olla-message]");
      if (!element) return;
      element.hidden = !message;
      element.textContent = message;
      element.classList.toggle("error", isError);
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll("[data-olla-root]").forEach((root) => new OllaProductPage(root));
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initialize());
  } else {
    initialize();
  }
  document.addEventListener("shopify:section:load", (event) => initialize(event.target));
})();
